import { useState, useEffect, useRef, useCallback } from "react";
import { tokenStore } from "@/lib/api";
import {
  Radio, WifiOff, Clock,
  RefreshCw, Activity, Zap, Database
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDuration(s) {
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
}

function formatBytes(b) {
  if (!b) return "0 B";
  if (b >= 1_000_000) return `${(b / 1_000_000).toFixed(1)} MB`;
  if (b >= 1_000) return `${(b / 1_000).toFixed(1)} KB`;
  return `${b} B`;
}

// ── KPI Stat Card ─────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, testid, iconClass }) {
  return (
    <div
      className="bg-white rounded border border-slate-200 p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded flex items-center justify-center ${iconClass || "bg-slate-100"}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <span className="text-xs text-slate-500 font-medium">{label}</span>
      </div>
      <p className="text-xl font-bold text-slate-900 font-mono tracking-tight">{value}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function QRNGLiveMonitor() {
  const [connected, setConnected] = useState(false);
  const [sessionSecs, setSessionSecs] = useState(0);
  const [latestEvent, setLatestEvent] = useState(null);
  const [eventLog, setEventLog] = useState([]);

  const esRef = useRef(null);
  const timerRef = useRef(null);

  const stopTimer = () => clearInterval(timerRef.current);

  const connectSSE = useCallback(() => {
    // Close existing connection but KEEP state (don't blank the screen)
    esRef.current?.close();
    stopTimer();

    const token = tokenStore.getAccess();
    if (!token) return;

    const es = new EventSource(
      `${API}/api/v1/entropy/live-stream?token=${encodeURIComponent(token)}`
    );
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "connected") {
          setConnected(true);
          // Only reset session timer on a fresh connect (not reconnect with stale data)
          setSessionSecs(0);
          stopTimer();
          timerRef.current = setInterval(() => setSessionSecs((s) => s + 1), 1000);
          return;
        }
        if (data.type === "heartbeat") return;
        if (data.type === "push") {
          // Historical burst events: pre-populate KPIs and log but don't count in session
          if (data.historical) {
            setLatestEvent((prev) => prev ?? data);  // only fill if nothing yet
            setEventLog((prev) => {
              if (prev.length > 0) return prev;      // skip if we already have live data
              return [data, ...prev].slice(0, 80);
            });
            return;
          }
          // Live event
          setLatestEvent(data);
          setEventLog((prev) => [data, ...prev].slice(0, 80));
        }
      } catch (_) {}
    };

    es.onerror = () => {
      setConnected(false);
      es.close();
      // Fast reconnect (1s) so the blank period is minimal
      setTimeout(() => connectSSE(), 1000);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    connectSSE();
    return () => { esRef.current?.close(); stopTimer(); };
  }, [connectSSE]);

  return (
    <div data-testid="qrng-live-monitor">

      {/* ── Page Header ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
            Live QRNG Monitor
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Real-time entropy verification — NIST SP 800-90B statistical test suite
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Session timer */}
          <div className="flex items-center gap-1.5 text-sm text-slate-500 mr-2">
            <Clock className="w-4 h-4" />
            <span className="font-mono" data-testid="session-timer">
              {formatDuration(sessionSecs)}
            </span>
          </div>

          {/* Reconnect */}
          <button
            onClick={() => {
              // Explicit reconnect: clear stale data and start fresh
              setLatestEvent(null);
              setEventLog([]);
              connectSSE();
            }}
            className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-3 py-2 rounded"
            data-testid="reconnect-btn"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Reconnect
          </button>

          {/* Status badge */}
          {connected ? (
            <Badge
              className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-medium"
              data-testid="sse-connection-status"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 inline-block animate-pulse" />
              Streaming
            </Badge>
          ) : (
            <Badge
              className="bg-slate-100 text-slate-500 border border-slate-200 text-xs font-medium"
              data-testid="sse-connection-status"
            >
              <WifiOff className="w-3 h-3 mr-1.5" />
              Disconnected
            </Badge>
          )}
        </div>
      </div>

      {/* ── KPI Row ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={Zap}
          label="QCNR"
          value={latestEvent?.qcnr != null ? (latestEvent.qcnr / 1000).toFixed(3) : "—"}
          sub="Quantum-to-Classical Noise Ratio"
          testid="kpi-qcnr"
          iconClass="bg-cyan-50 text-cyan-600"
        />
        <StatCard
          icon={Activity}
          label="Speed"
          value={latestEvent?.speed_mbps != null ? `${latestEvent.speed_mbps} Mbps` : "—"}
          sub="Entropy Throughput"
          testid="kpi-speed"
          iconClass="bg-violet-50 text-violet-600"
        />
        <StatCard
          icon={Database}
          label="Pool Fill"
          value={latestEvent?.pool_fill_pct != null ? `${latestEvent.pool_fill_pct}%` : "—"}
          sub="Redis Entropy Pool"
          testid="kpi-pool-fill"
          iconClass="bg-amber-50 text-amber-600"
        />
        <StatCard
          icon={Radio}
          label="Events"
          value={eventLog.length}
          sub="Push events this session"
          testid="kpi-events"
          iconClass="bg-emerald-50 text-emerald-600"
        />
      </div>

      {/* ── Live Event Log ────────────────────────────────────────────── */}
      <div
        className="bg-white rounded border border-slate-200"
        data-testid="live-event-log"
      >
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Live Entropy Push Stream</h2>
            <p className="text-xs text-slate-400 mt-0.5">Adaptive — updates on each hardware push</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span
              className={`w-2 h-2 rounded-full inline-block ${
                connected ? "bg-emerald-400 animate-pulse" : "bg-slate-300"
              }`}
            />
            {eventLog.length} events
          </div>
        </div>

        {/* Table header */}
        {eventLog.length > 0 && (
          <div className="grid grid-cols-6 gap-4 px-5 py-2 border-b border-slate-100 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            <span>Timestamp</span>
            <span className="col-span-2">Device</span>
            <span>Data</span>
            <span>Pool / Speed</span>
            <span className="text-right">Min-Entropy</span>
          </div>
        )}

        <div className="overflow-y-auto" style={{ maxHeight: 280 }}>
          {eventLog.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">
              {connected
                ? "Waiting for entropy push events from hardware..."
                : "Connecting to live stream..."}
            </div>
          ) : (
            eventLog.map((ev, i) => {
              const h = ev.nist_stats?.min_entropy;
              const hPass = h != null && h >= 0.95;
              return (
                <div
                  key={`${ev.timestamp}-${i}`}
                  className={`grid grid-cols-6 gap-4 px-5 py-2.5 border-b border-slate-50 text-xs items-center transition-colors ${
                    i === 0 ? "bg-cyan-50/40" : "hover:bg-slate-50"
                  }`}
                  data-testid={i === 0 ? "latest-event-row" : undefined}
                >
                  <span className="text-slate-400 font-mono tabular-nums">
                    {new Date(ev.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="col-span-2 text-slate-700 font-medium truncate flex items-center gap-1.5">
                    {ev.simulated && (
                      <span className="text-[9px] bg-amber-100 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded font-semibold">
                        SIM
                      </span>
                    )}
                    {ev.device_id}
                  </span>
                  <span className="text-slate-600">
                    {formatBytes(ev.bytes_pushed ?? 0)}
                  </span>
                  <span className="text-slate-400 tabular-nums">
                    {ev.pool_fill_pct ?? 0}% · {ev.speed_mbps ?? 0} Mbps
                  </span>
                  <span className={`text-right font-semibold font-mono ${
                    h == null ? "text-slate-300" : hPass ? "text-emerald-600" : "text-red-500"
                  }`}>
                    {h != null ? h.toFixed(4) : "—"}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
